/**
 * Spotlight overlay (#124).
 *
 * A fixed, full-screen dim that leaves a "hole" cut around the active anchor's
 * bounding box, drawing the eye to the one element the current step is about.
 *
 * Implementation: rather than an SVG mask, we paint FOUR dim panels — above /
 * below / left / right of the hole. The hole itself has no overlay element.
 *
 * IMPORTANT (safety + user-control, #124): every dim panel is
 * `pointer-events-none` — purely visual. The tour guides over the REAL UI, so
 * the user must be able to click ANY real control at any time (including the
 * Close button or Next/Back inside a dialog the tour is highlighting). A
 * click-capturing dim could trap the user or swallow a needed click, so we
 * never capture pointer events. The ring is also non-interactive; clicks fall
 * straight through to the real page underneath.
 *
 * The rect is supplied by the parent (TourOverlay → useTourAnchor), which
 * recomputes it on resize and scroll; this component is purely presentational
 * and re-renders when that rect changes.
 *
 * z-index: the dim panels + ring sit at z-40/z-50, within Tailwind's standard
 * scale per the token-only policy.
 */

import { cn } from "@/lib/ui/cn";

/** Padding around the anchor box, in px, so the hole isn't flush to the edge. */
const PAD = 8;

export interface SpotlightProps {
  /** The anchor's viewport-space rect, or null while it resolves. */
  rect: DOMRect | null;
}

export function Spotlight({ rect }: SpotlightProps) {
  // Non-interactive dim: pointer-events-none so real controls stay clickable.
  const dim = "pointer-events-none fixed z-40 bg-on-surface/50 transition-all";

  // Until the anchor resolves, dim the whole screen (single panel).
  if (!rect) {
    return <div aria-hidden className={cn(dim, "inset-0")} />;
  }

  const top = Math.max(0, rect.top - PAD);
  const left = Math.max(0, rect.left - PAD);
  const right = rect.right + PAD;
  const bottom = rect.bottom + PAD;
  const holeWidth = right - left;
  const holeHeight = bottom - top;

  return (
    <div aria-hidden>
      {/* Top panel — full width, above the hole */}
      <div className={dim} style={{ top: 0, left: 0, right: 0, height: top }} />
      {/* Bottom panel — full width, below the hole */}
      <div className={dim} style={{ top: bottom, left: 0, right: 0, bottom: 0 }} />
      {/* Left panel — between top and bottom, left of the hole */}
      <div className={dim} style={{ top, left: 0, width: left, height: holeHeight }} />
      {/* Right panel — between top and bottom, right of the hole */}
      <div className={dim} style={{ top, left: right, right: 0, height: holeHeight }} />
      {/* Ring around the hole — non-interactive, so the anchor underneath stays
          clickable through the hole. */}
      <div
        aria-hidden
        className="pointer-events-none fixed z-50 rounded-lg ring-2 ring-brand ring-offset-2 ring-offset-surface transition-all"
        style={{ top, left, width: holeWidth, height: holeHeight }}
      />
    </div>
  );
}
